import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'
import { db, auth } from '@/config/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { collection, doc, getDoc, getDocs, limit, query, updateDoc, serverTimestamp, where } from 'firebase/firestore'
import { readSupportSession, isSupportSessionForCompany } from '@/utils/supportSession';

const normalizeSlug = (value) => (value || '')
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const resolveAccountId = (slug) => `acc_${normalizeSlug(slug)}`;

async function resolveAccountIdBySlug(slug) {
  const normalizedSlug = normalizeSlug(slug);
  if (!normalizedSlug) return null;

  const canonicalByConvention = resolveAccountId(normalizedSlug);
  try {
    const canonicalSnap = await getDoc(doc(db, `accounts/${canonicalByConvention}`));
    if (canonicalSnap.exists()) return canonicalByConvention;
  } catch (error) {
    if ((error && error.code) === 'permission-denied') return canonicalByConvention;
    throw error;
  }

  const slugFields = ['legacy_cliente_slug', 'legacy_slug', 'slug'];
  for (const fieldName of slugFields) {
    try {
      const q = query(
        collection(db, 'accounts'),
        where(fieldName, '==', normalizedSlug),
        limit(1),
      );
      const snap = await getDocs(q);
      if (!snap.empty) return snap.docs[0].id;
    } catch (error) {
      if ((error && error.code) === 'permission-denied') return canonicalByConvention;
      throw error;
    }
  }
  return null;
}

async function fetchUserByEmail(slug, email) {
  const raw = (email || '').toString().trim();
  if (!raw) return null;
  const accountId = await resolveAccountIdBySlug(slug);
  if (!accountId) return null;
  let snap = await getDoc(doc(db, `accounts/${accountId}/users/${raw}`));
  if (snap.exists()) return { snap, docId: raw };
  const lower = raw.toLowerCase();
  if (lower !== raw) {
    snap = await getDoc(doc(db, `accounts/${accountId}/users/${lower}`));
    if (snap.exists()) return { snap, docId: lower };
  }
  return null;
}

import Home from '@/views/site/Home.vue'
import SiteLogin from '@/views/site/Login.vue';
import Register from '@/views/site/Register.vue';
import SitePlans from '@/views/site/Plans.vue';
import NavBar from '../components/NavBarUnified.vue';
import ClientLogin from '@/views/client/Login.vue';
import NavBarEmpresa from '../components/NavBarUnified.vue';
import NavBarDashboard from '../components/NavBarUnified.vue';
import GrcHome from '@/views/grc/GrcHome.vue';
import ESG from '@/views/complaint/ESG.vue';
import RecrutamentoHome from '@/views/eia/RecrutamentoHome.vue';

const EmptyNamedView = {
  render() {
    return null;
  },
};

const markSSORedirectBlocked = (slug) => {
  if (!slug) return;
  try {
    localStorage.setItem("ssoRedirectBlocked", slug);
  } catch (_) {
    // ignore storage errors
  }
};

const isJustLoggedInWithSSO = (slug) => {
  if (!slug) return false;
  try {
    const payload = JSON.parse(localStorage.getItem('justLoggedInWithSSO') || '{}');
    if (payload.slug && payload.slug.toLowerCase() === slug.toLowerCase()) {
      if (payload.expiresAt && Number(payload.expiresAt) > Date.now()) {
        return true;
      }
    }
  } catch (_) {
    /* ignore */
  }
  return false;
};

const companyAuthGuard = async (to, from, next) => {
  try {
    const firebaseUser = auth.currentUser;
    const hasUserSession = Boolean(firebaseUser);
    const session = readSupportSession();
    const slug = (to.params.company || '').toString().trim().toLowerCase();
    if (isSupportSessionForCompany(session, slug)) {
      const impersonated = (session && (session.impersonatedDocId || session.impersonatedEmail) || '').toString();
      if (!impersonated) return next('/admin/suporte/login');
      const supportUser = await fetchUserByEmail(slug, impersonated);
      if (!supportUser || supportUser.snap.data()?.approved !== true) {
        await store.dispatch('clearSupportSession');
        return next('/admin/suporte/login');
      }
      return next();
    }
    if (!hasUserSession) return next({ path: `/${slug}/entrar` });
    return next();
  } catch (_) {
    return next({ path: `/${to.params.company}/entrar` });
  }
};

const hasAuthenticatedSession = () => {
  return Boolean(auth.currentUser);
};


const isMeSurfaceHost = () => {
  if (typeof window === 'undefined' || !window.location) return false;
  const hostname = (window.location.hostname || '').toString().trim().toLowerCase();
  if (!hostname) return false;
  if (hostname === 'due-me-stg.web.app') return true;
  if (hostname === 'due-me-stg.firebaseapp.com') return true;
  if (hostname === 'me.stg.due.app.br') return true;
  return hostname === 'me.due.app.br' || hostname.endsWith('.me.due.app.br');
};

const isUsSurfaceHost = () => {
  if (typeof window === 'undefined' || !window.location) return false;
  const hostname = (window.location.hostname || '').toString().trim().toLowerCase();
  if (!hostname) return false;
  if (hostname === 'due-us-stg.web.app') return true;
  if (hostname === 'due-us-stg.firebaseapp.com') return true;
  if (hostname === 'us.stg.due.app.br') return true;
  return hostname === 'us.due.app.br' || hostname.endsWith('.us.due.app.br');
};

const waitForAuthenticatedSession = async (timeoutMs = 3000) => {
  if (hasAuthenticatedSession()) return true;

  // Aguarda o primeiro estado de auth para evitar tela em branco prolongada.
  return new Promise((resolve) => {
    let settled = false;
    let unsubscribe = () => {};
    const finish = (value) => {
      if (settled) return;
      settled = true;
      try { unsubscribe(); } catch (_) { /* ignore */ }
      resolve(Boolean(value));
    };

    try {
      unsubscribe = onAuthStateChanged(
        auth,
        (user) => finish(Boolean(user)),
        () => finish(false),
      );
    } catch (_) {
      finish(false);
      return;
    }

    setTimeout(() => {
      finish(hasAuthenticatedSession());
    }, timeoutMs);
  });
};

const meShellGuard = async (to, from, next) => {
  const hasSession = await waitForAuthenticatedSession();
  if (!hasSession) return next('/entrar');
  await store.dispatch('setActiveContext', { surface: 'me', source: 'router:me' });
  return next();
};

const usShellGuard = async (to, from, next) => {
  const hasSession = await waitForAuthenticatedSession();
  if (!hasSession) return next('/entrar');

  const accountIdFromQuery = ((to.query && to.query.accountId) || '').toString().trim() || null;
  const storedActiveAccountId = (store.getters && store.getters.activeAccountId) || null;
  const targetAccountId = accountIdFromQuery || storedActiveAccountId;
  if (!targetAccountId) return next('/us/seletor');

  const contextSet = await store.dispatch('setActiveContext', {
    surface: 'us',
    accountId: targetAccountId,
    source: accountIdFromQuery ? 'router:us-query' : 'router:us',
  });
  if (!contextSet) return next('/us/seletor');
  return next();
};

const radarWorkspaceGuard = async (to, from, next) => {
  const hasSession = await waitForAuthenticatedSession();
  if (!hasSession) return next('/entrar');
  return next();
};

const getRouteCompanySlug = (to) => (
  (to?.params && (to.params.company || to.params.cliente) || '')
    .toString()
    .trim()
    .toLowerCase()
);

const isCompanyLoginRoute = (to) => (
  to?.name === 'CompanyLogin' || /^\/[^/]+\/entrar$/.test(to?.path || '')
);

const evaluateCompanyRouteAccess = async (to) => {
  const slug = getRouteCompanySlug(to);
  const hasClienteParam = Boolean(slug);
  if (!hasClienteParam || isCompanyLoginRoute(to)) {
    return { allow: true, redirect: null };
  }

  const u = (store.getters && store.getters.user) || null;
  const userEmailFromStore = (store.getters && ((store.getters.userEmail || store.getters.email) || '')) || '';
  const firebaseUser = auth.currentUser;
  const firebaseEmail = (firebaseUser && (firebaseUser.email || firebaseUser.uid || '') || '').toString().toLowerCase();
  const resolvedEmail = (
    (u?.email || (u?.data && u.data.email) || userEmailFromStore || firebaseEmail) || ''
  ).toLowerCase();
  const hasUserSession = Boolean(firebaseUser);

  if (hasUserSession) {
    localStorage.removeItem('justLoggedInWithSSO');
  } else if (userEmailFromStore) {
    try { store.commit('CLEAR_EMAIL'); } catch (_) { /* ignore */ }
  }

  const supportSession = readSupportSession();
  if (!hasUserSession && !isSupportSessionForCompany(supportSession, slug)) {
    if (isJustLoggedInWithSSO(slug)) return { allow: true, redirect: null };
    markSSORedirectBlocked(slug);
    return { allow: false, redirect: { path: `/${slug}/entrar` } };
  }

  if (isSupportSessionForCompany(supportSession, slug)) {
    const impersonated = (supportSession && (supportSession.impersonatedDocId || supportSession.impersonatedEmail) || '').toString();
    if (!impersonated) {
      await store.dispatch('clearSupportSession');
      return { allow: false, redirect: '/admin/suporte/login' };
    }
    try {
      const supportUser = await fetchUserByEmail(slug, impersonated);
      if (!supportUser || supportUser.snap.data()?.approved !== true) {
        await store.dispatch('clearSupportSession');
        return { allow: false, redirect: '/admin/suporte/login' };
      }
    } catch (_) {
      await store.dispatch('clearSupportSession');
      return { allow: false, redirect: '/admin/suporte/login' };
    }
    return { allow: true, redirect: null };
  }

  if (!resolvedEmail) {
    console.warn("[ROUTER] redirecionando porque usuário sem email", slug);
    markSSORedirectBlocked(slug);
    return { allow: false, redirect: { path: `/${slug}/entrar` } };
  }

  if (!hasUserSession && slug) {
    return { allow: false, redirect: { path: `/${slug}/entrar` } };
  }

  return { allow: true, redirect: null };
};

const routes = [
  {
    path: '/:company/entrar',
    name: 'login-cliente',
      components:{
        default: ClientLogin,
        NavBarEmpresa
      },
    // Redireciona para SSO se o cliente tiver ssoEnabled=true no Firestore
    beforeEnter: async (to, from, next) => {
      const slug = (to.params.company || '').toString().trim().toLowerCase();
      if (!slug) return next('/cliente-invalido');
      try {
        const accountId = await resolveAccountIdBySlug(slug);
        if (!accountId) return next('/cliente-invalido');
        const session = readSupportSession();
        if (isSupportSessionForCompany(session, slug)) {
          const impersonated = (session && (session.impersonatedDocId || session.impersonatedEmail) || '').toString();
          if (impersonated) {
            const supportUser = await fetchUserByEmail(slug, impersonated);
            if (supportUser && supportUser.snap.data()?.approved === true) {
              return next({ path: `/${slug}` });
            }
          }
        }
        const supportBypass = to.query && to.query.support === '1';
        if (supportBypass) {
          const accessSnap = await getDoc(doc(db, `accounts/${accountId}/config/supportAccess`));
          const allowSSOBypass = accessSnap.exists() && accessSnap.data()?.allowSSOBypass === true;
          if (allowSSOBypass) return next();
        }
        let ssoEnabled = false;
        try {
          const ssoSnap = await getDoc(doc(db, `accounts/${accountId}/config/sso`));
          if (ssoSnap.exists()) {
            ssoEnabled = ssoSnap.data()?.enabled === true;
          }
        } catch (error) {
          console.warn('Falha ao carregar config SSO do cliente; usando flag legado.', error);
        }
        if (ssoEnabled) {
          to.meta = to.meta || {};
          to.meta.ssoEnabled = true;
        }
      // O redirecionamento para o SSO é feito no componente de login.
      } catch (error) {
        console.warn('Não foi possível verificar ssoEnabled; seguindo para login padrão.', error);
      }
      return next();
    }
},
  {
    path: '/cliente-invalido',
    name: 'InvalidCompany',
    components:{
      default: () => import('@/views/site/InvalidCompany.vue'),
      NavBar
    }
  },
  {
    path: '/admin/suporte/login',
    name: 'SupportLogin',
    components:{
      default: () => import('@/views/admin/SupportLogin.vue'),
      NavBar
    }
  },
  {
    path: '/admin/suporte',
    name: 'SupportAccess',
    components:{
      default: () => import('@/views/admin/SupportAccess.vue'),
      NavBar
    },
    meta: { requiresSupportUser: true }
  },
  {
    path: '/admin/suporte/tickets',
    name: 'SupportTicketsAdmin',
    components:{
      default: () => import('@/views/admin/SupportTickets.vue'),
      NavBar
    },
    meta: { requiresSupportUser: true }
  },
  {
    path: '/admin/suporte/tickets/:company/:ticketId',
    name: 'SupportTicketAdminDetail',
    components:{
      default: () => import('@/views/support/TicketDetail.vue'),
      NavBar
    },
    meta: { requiresSupportUser: true }
  },
  {
    path: '/entrar',
    name: 'login',
    components:{
      default: SiteLogin,
      NavBar
    }
  },
  {
      path: '/registrar',
      name: 'Register',
      components:{
        default: Register,
        NavBar
      },
      beforeEnter: (to, from, next) => {
        if (hasAuthenticatedSession()) return next('/me');
        return next();
      }
  },
  {
      path: '/cadastro',
      name: 'Signup',
      components:{
        default: Register,
        NavBar
      },
      beforeEnter: (to, from, next) => {
        if (hasAuthenticatedSession()) return next('/me');
        return next();
      }
  },
  {
    path: '/',
    name: 'Home',
    components:{
      default: () => {
        if (isMeSurfaceHost()) return import('@/views/shell/MeShell.vue');
        if (isUsSurfaceHost()) return import('@/views/shell/UsShell.vue');
        return Promise.resolve(Home);
      },
      NavBar: () => {
        if (isMeSurfaceHost() || isUsSurfaceHost()) return Promise.resolve(EmptyNamedView);
        return Promise.resolve(NavBar);
      },
    },
    beforeEnter: async (to, from, next) => {
      if (isMeSurfaceHost()) return meShellGuard(to, from, next);
      if (isUsSurfaceHost()) return usShellGuard(to, from, next);
      if (!hasAuthenticatedSession()) return next();
      const activeSurface = store.getters && store.getters.activeSurface;
      const activeAccountId = store.getters && store.getters.activeAccountId;
      if (activeSurface === 'us' && activeAccountId) return next('/us');
      if (activeSurface === 'me') return next('/me');
      return next();
    }
  },
  {
    path: '/me',
    name: 'MeShell',
    components: {
      default: () => import('@/views/shell/MeShell.vue')
    },
    beforeEnter: meShellGuard
  },
  {
    path: '/billing/success',
    name: 'BillingSuccess',
    components: {
      default: () => import('@/views/shell/BillingSuccess.vue')
    },
    beforeEnter: meShellGuard
  },
  {
    path: '/billing/cancel',
    name: 'BillingCancel',
    components: {
      default: () => import('@/views/shell/BillingCancel.vue')
    },
    beforeEnter: meShellGuard
  },
  {
    path: '/us',
    name: 'UsShell',
    components: {
      default: () => import('@/views/shell/UsShell.vue')
    },
    beforeEnter: usShellGuard
  },
  {
    path: '/us/seletor',
    name: 'UsAccountSelector',
    components: {
      default: () => import('@/views/shell/UsAccountSelector.vue')
    },
    beforeEnter: async (to, from, next) => {
      const hasSession = await waitForAuthenticatedSession();
      if (!hasSession) return next('/entrar');
      return next();
    }
  },
  {
    path: '/radar/workspace',
    name: 'RadarWorkspace',
    components: {
      default: () => import('@/views/radar/Workspace.vue'),
      NavBar
    },
    beforeEnter: radarWorkspaceGuard
  },
  {
    path: '/:company/principal',
    name: 'LoggedHome',
    components:{
      default: () => import('@/views/client/LoggedHome.vue'),
      NavBar
    }
  },
  {
    path: '/dna',
    name: 'Dna',
    components:{
      default: () => import('@/views/site/Dna.vue'),
      NavBar
    }
  },
  {
    path: '/servicos',
    name: 'Services',
    components:{
      default: () => import('@/views/site/Services.vue'),
      NavBar
    }
  },
  {
    path: '/planos',
    name: 'SitePlans',
    components: {
      default: SitePlans,
      NavBar
    }
  },
  // {
  //   path: '/sustentabilidade',
  //   name: 'Sestainability',
  //   components:{
  //     default: () => import('@/views/site/Sustainability.vue'),
  //     NavBar
  //   }
  // },
  {
    path: '/:company/protecao-de-dados',
    name: 'DataProtection',
    components:{
      default: () => import('@/views/client/DataProtection.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company',
    name: 'CompanyHome',
    components:{
      default: () => import('@/views/shell/EnterpriseShell.vue'),
      NavBarEmpresa,
    },
    beforeEnter: companyAuthGuard
  },
  {
    path: '/:company/grc',
    name: 'CompanyGrcHome',
    components: {
      default: GrcHome,
      NavBarEmpresa,
    },
    beforeEnter: companyAuthGuard
  },
  {
    path: '/:company/esg',
    name: 'CompanyEsgHome',
    components: {
      default: ESG,
      NavBarEmpresa,
    },
    beforeEnter: companyAuthGuard
  },
  {
    path: '/:company/rh',
    name: 'CompanyRhHome',
    components: {
      default: RecrutamentoHome,
      NavBarEmpresa,
    },
    beforeEnter: companyAuthGuard
  },
  {
    path: '/:company/suporte',
    name: 'SupportTickets',
    components: {
      default: () => import('@/views/support/Tickets.vue'),
      NavBarEmpresa,
    },
    beforeEnter: companyAuthGuard,
  },
  {
    path: '/:company/suporte/novo',
    name: 'SupportTicketCreate',
    components: {
      default: () => import('@/views/support/TicketCreate.vue'),
      NavBarEmpresa,
    },
    beforeEnter: companyAuthGuard,
  },
  {
    path: '/:company/suporte/:ticketId',
    name: 'SupportTicketDetail',
    components: {
      default: () => import('@/views/support/TicketDetail.vue'),
      NavBarEmpresa,
    },
    beforeEnter: companyAuthGuard,
  },
  {
    path: '/:company/eia/recrutamento',
    name: 'EIARecrutamentoHome',
    redirect: (to) => `/${to.params.company}/eia/recrutamento/processos`,
  },
  {
    path: '/:company/eia/:type/processos',
    name: 'EIAProcessos',
    components:{
      default: () => import('@/views/eia/Index.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/eia/:type',
    name: 'EIA',
    components:{
      default: () => import('@/views/eia/Index.vue'),
      NavBarEmpresa,
    }
  },
  // Redireciona URLs legadas /eia/chat para o subdomínio atual do EIA
  {
    path: '/eia/chat',
    name: 'LegacyEiaChat',
    component: () => import('@/views/eia/LegacyChatRedirect.vue')
  },
  {
    path: '/:company/eia/chat',
    name: 'LegacyCompanyEiaChat',
    component: () => import('@/views/eia/LegacyChatRedirect.vue')
  },
  {
    path: '/:company/previews',
    name: 'EIAPreviews',
    components:{
      default: () => import('@/views/eia/Previews.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/normativos',
    name: 'NormativosPreviews',
    components:{
      default: () => import('@/views/complaint/Normativos.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/codigo-de-conduta',
    name: 'CodigoDeConduta',
    components:{
      default: () => import('@/views/complaint/CodigoDeConduta.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/gestao-de-normativos',
    name: 'GestaoDeNormativos',
    components: {
      default: () => import('@/views/complaint/GestaoDeNormativos.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/politica_de_integridade',
    name: 'PoliticaDeIntegridade',
    components: {
      default: () => import('@/views/complaint/PoliticaDeIntegridade.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/programa-de-compliance',
    name: 'ProgramaDeCompliance',
    components: {
      default: () => import('@/views/complaint/ProgramaDeCompliance.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/gestao-de-normativos/cadastro',
    name: 'CadastroNormativos',
    components: {
      default: () => import('@/views/complaint/CadastroNormativos.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/treinamentos',
    name: 'TreinamentosCards',
    components:{
      default: () => import('@/views/complaint/TreinamentosCards.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/treinamentos/politica-de-integridade',
    name: 'TreinamentosPoliticaDeIntegridade',
    components:{
      default: () => import('@/views/complaint/TreinamentoPoliticaDeIntegridade.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/treinamentos/treinamentos',
    name: 'TreinamentosPreviews',
    components:{
      default: () => import('@/views/complaint/Treinamentos.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/treinamentos/cadastro',
    name: 'TreinamentosCadastro',
    components:{
      default: () => import('@/views/complaint/TreinamentosCadastro.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/treinamentos/cadastro-treinamentos',
    name: 'TreinamentosTrilhas',
    components:{
      default: () => import('@/views/complaint/TreinamentosTrilhas.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/treinamentos/kpi',
    name: 'TreinamentosKPI',
    components:{
      default: () => import('@/views/complaint/TreinamentosKPI.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/gestao-de-riscos',
    name: 'GestaoDeRiscos',
    components:{
      default: () => import('@/views/complaint/GestaoDeRiscos.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/gestao-de-riscos/riscos',
    name: 'MatrizDeRiscos',
    components:{
      default: () => import('@/views/complaint/Riscos.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/gestao-de-riscos/kpi',
    name: 'KPIDeRiscos',
    components:{
      default: () => import('@/views/complaint/RiscosKPI.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/gestao-de-riscos/regras-de-impacto',
    name: 'RegrasDeImpacto',
    components:{
      default: () => import('@/views/complaint/RiscosRegras.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/gestao-de-riscos/matriz-de-calor',
    name: 'MatrizDeCalor',
    components:{
      default: () => import('@/views/complaint/RiscosMatriz.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/gestao-de-riscos/fluxograma-de-processos',
    name: 'FluxogramaDeProcessos',
    components:{
      default: () => import('@/views/complaint/ListaFluxogramas.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/gestao-de-riscos/fluxograma-de-processos/:fluxoId',
    name: 'FluxogramaEditor',
    components: {
      default: () => import('@/views/complaint/RiscosFluxograma.vue'),
      NavBarEmpresa,
    },
    props: {
      default: true,
      NavBarEmpresa: false
    }
  },
  {
    path: '/:company/gestao-de-riscos/controles-internos',
    name: 'ControlesInternos',
    components:{
      default: () => import('@/views/complaint/ControlesInternos.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/terceiros',
    name: 'DueDiligenceTerceiros',
    components:{
      default: () => import('@/views/terceiros/Terceiros.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/terceiros/novo',
    name: 'TerceiroNovo',
    components:{
      default: () => import('@/views/terceiros/TerceiroNovo.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/terceiros/publico/:terceiroId',
    name: 'TerceiroPublico',
    components:{
      default: () => import('@/views/terceiros/TerceiroPublico.vue'),
      NavBar,
    },
    meta: { publicAccess: true }
  },
  {
    path: '/:company/terceiros/:terceiroId',
    name: 'TerceiroDetalhe',
    components:{
      default: () => import('@/views/terceiros/TerceiroDetalhe.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/admin/modules/:company',
    name: 'AdminModulesForClient',
    components: {
      default: () => import('@/views/complaint/AdminModules.vue'),
      NavBar
    },
    meta: { requiresPlatformAdmin: true }
  },
  {
    path: '/admin',
    name: 'Admin',
    components: {
      default: () => import('@/views/admin/AdminGate.vue'),
      NavBar
    }
  },
  // Relatórios (novo modelo)
  {
    path: '/:cliente/eia/pesquisa-de-compliance/relatorio/:denuncia',
    name: 'RelatorioPesquisaDeCompliance',
    components: {
      default: () => import('@/views/complaint/report/types/RelatorioPesquisaDeCompliance.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:cliente/eia/recrutamento/relatorio/:denuncia',
    name: 'RecrutamentoReport',
    components: {
      default: () => import('@/views/complaint/report/types/RecrutamentoReport.vue'),
      NavBarEmpresa,
    }
  },
  // Relatório legado (caminho alternativo para comparação)
  {
    path: '/:company/eia/:type/relatorio-legacy/:denuncia',
    name: 'EIARelatorioLegado',
    components: {
      default: () => import('@/views/eia/Relatorio.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/eia/pesquisa-de-compliance/relatorio/:denuncia',
    name: 'EIARelatorioCompliance',
    components:{
      default: () => import('@/views/eia/RelatorioCompliance.vue'),
      NavBarEmpresa,
    },
    props: { default: { type: 'pesquisa-de-compliance' } }
  },
  {
    path: '/:company/eia/pesquisa-de-clima/relatorio/:denuncia',
    name: 'EIARelatorioClima',
    components:{
      default: () => import('@/views/eia/RelatorioClima.vue'),
      NavBarEmpresa,
    },
    props: { default: { type: 'pesquisa-de-clima' } }
  },
  {
    path: '/:company/eia/diversidade/relatorio/:denuncia',
    name: 'EIARelatorioDiversidade',
    components:{
      default: () => import('@/views/eia/RelatorioDiversidade.vue'),
      NavBarEmpresa,
    },
    props: { default: { type: 'diversidade' } }
  },
  {
    path: '/:company/eia/satisfacao/relatorio/:denuncia',
    name: 'EIARelatorioSatisfacao',
    components:{
      default: () => import('@/views/eia/RelatorioSatisfacao.vue'),
      NavBarEmpresa,
    },
    props: { default: { type: 'satisfacao' } }
  },
  {
    path: '/:company/eia/recrutamento/form-pesquisa',
    name: 'EIAFormRecrutamento',
    components: {
      default: () => import('@/views/eia/FormRecrutamento.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/eia/pesquisa-de-compliance/form/:denuncia',
    name: 'EIAFormCompliance',
    components:{
      default: () => import('@/views/eia/FormCompliance.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/eia/pesquisa-de-clima/form/:denuncia',
    name: 'EIAFormClima',
    components:{
      default: () => import('@/views/eia/FormClima.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/eia/diversidade/form/:denuncia',
    name: 'EIAFormDiversidade',
    components:{
      default: () => import('@/views/eia/FormDiversidade.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/eia/satisfacao/form/:denuncia',
    name: 'EIAFormSatisfacao',
    components:{
      default: () => import('@/views/eia/FormSatisfacao.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/eia/:type/form-pesquisa',
    name: 'EIAFormEntrevista',
    components:{
      default: () => import('@/views/eia/FormEntrevista.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/eia/:type/form/:denuncia',
    name: 'EIAForm',
    components:{
      default: () => import('@/views/eia/Form.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/eia/:type/relatorio/:denuncia',
    name: 'EIARelatorio',
    components:{
      default: () => import('@/views/eia/Relatorio.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:cliente/eia/pesquisa-de-compliance/relatorio/:denuncia',
    name: 'RelatorioPesquisaDeCompliance',
    components:{
      default: () => import('@/views/complaint/report/types/RelatorioPesquisaDeCompliance.vue'),
      NavBarEmpresa,
    },
    props: {
      default: route => ({
        cliente: route.params.cliente,
        docId: route.params.denuncia,
        objective: ''
      })
    }
  },
  {
    path: '/:cliente/eia/recrutamento/relatorio/:denuncia',
    name: 'RecrutamentoReport',
    components:{
      default: () => import('@/views/complaint/report/types/RecrutamentoReport.vue'),
      NavBarEmpresa,
    },
    props: {
      default: route => ({
        cliente: route.params.cliente,
        docId: route.params.denuncia,
        objective: ''
      })
    }
  },
  {
    path: '/:company/eia/:type/indicadores',
    name: 'EIAIndicadores',
    components:{
      default: () => import('@/views/eia/Indicadores.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/admin',
    name: 'AdminArea',
    components:{
      default: () => import('@/views/complaint/Admin.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/perfil',
    name: 'CompanyProfileSettings',
    components:{
      default: () => import('@/views/complaint/ProfileSettings.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/sistema',
    name: 'CompanyReportSettings',
    components:{
      default: () => import('@/views/complaint/ReportSettings.vue'),
      NavBarEmpresa,
    }
  },

    {
    path: '/:company/admin/usuarios',
    name: 'UsuariosAdmin',
    components:{
      default: () => import('@/views/complaint/ActiveUsers.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/admin/modules/',
    name: 'AdminModules',
    components:{
      default: () => import('@/views/complaint/AdminModules.vue'),
      NavBarEmpresa,
    },
    meta: { requiresCompanyAdmin: true }
  },
  {
    path: '/:company/admin/faturamento',
    name: 'AdminBilling',
    components:{
      default: () => import('@/views/complaint/AdminBilling.vue'),
      NavBarEmpresa,
    },
    meta: { requiresCompanyAdmin: true }
  },
  {
    path: '/:company/admin/branding',
    name: 'AdminBranding',
    components:{
      default: () => import('@/views/complaint/AdminBranding.vue'),
      NavBarEmpresa,
    },
    meta: { requiresCompanyAdmin: true }
  },
  {
    path: '/:company/admin/group_registration/',
    name: 'RegisterGroup',
    components:{
      default: () => import('@/views/complaint/RegisterGroup.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/admin/add_emails/',
    name: 'RegisterEmails',
    components:{
      default: () => import('@/views/complaint/RegisterEmails.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/admin/cadastro-de-setores',
    name: 'CadastroDeSetores',
    components:{
      default: () => import('@/views/complaint/RegisterSetores.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/check/painel',
    name: 'BackgroundCheck',
    components: {
      default: () => import('@/views/check/Home.vue'),
      NavBarDashboard
    }
  },
  {
    path: '/:company/riscos/painel',
    name: 'RiskManage',
    components: {
      default: () => import('@/views/risk/Home.vue'),
      NavBarDashboard
    }
  },
    {
      path: '/:company/sugestao',
      name: 'Sugestion',
      components:{
        default: () => import('@/views/client/Sugestion.vue'),
        NavBarEmpresa,
      },
      meta: { publicAccess: true }
    },
  // {
  //   path: '/cliente/:company',
  //   name: 'Cliente',
  //   component: () => import('../views/ClientView.vue')
  // },
  {
    path: '/tutorial-lgpd',
    name: 'TutorialLGPD',
    components: {
      default: () => import('@/views/client/TutorialLGPD.vue'),
      NavBar
    }
  },
  {
    path: '/contato',
    name: 'Leads',
    components:{
      default: () => import('@/views/site/Leads.vue'),
      NavBar,
    }
  },
  {
    path: '/termos-de-uso',
    name: 'termos-de-uso',
    components:{
      default: () => import('@/views/TermosDeUso.vue'),
      NavBar,
    }
  },
  {
    path: '/:company/manual-de-compliance',
    name: 'manual-de-compliance',
    components:{
      default: () => import('@/views/ManualDeCompliance.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/politica-de-privacidade',
    name: 'politica-de-privacidade',
    components:{
      default: () => import('@/views/PoliticaDePrivacidade.vue'),
      NavBar,
    }
  },
    {
      path: '/:company/denuncias',
      name: 'DenunciasCards',
      components:{
        default: () => import('@/views/complaint/Denuncias.vue'),
        NavBarEmpresa,
      },
      meta: { publicAccess: true }
    },
    {
      path: '/:company/denuncia',
      name: 'denuncia-form',
      components:{
        default: () => import('@/views/complaint/Form.vue'),
        NavBarEmpresa,
      },
      meta: { publicAccess: true }
    },
    {
      path: '/:company/denuncia/painel',
      name: 'denuncia-painel',
      redirect: (to) => `/${to.params.company}/denuncias`,
      meta: { publicAccess: true }
    },
    {
      path: '/:company/denuncias/canal-de-denuncia',
      name: 'canal-de-denuncia',
      components:{
        default: () => import('../views/complaint/Report.vue'),
        NavBarEmpresa,
      },
      meta: { publicAccess: true }
    },
  {
    path: '/:company/denuncias/gestao',
    name: 'gestao-de-denuncia',
    components:{
      default: () => import('@/views/complaint/GestaoDenuncias.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/denuncias/gestao/denuncias-recebidas',
    name: 'denuncias-recebidas',
    components:{
      default: () => import('@/views/complaint/DenunciasRecebidas.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/denuncias/gestao/indicadores-de-performance/',
    name: 'indicadores-de-performance/',
    components:{
      default: () => import('@/views/complaint/DenunciasIndicadores.vue'),
      NavBarEmpresa,
    }
  },
  {
    path: '/:company/denuncias/gestao/plano-de-acao/',
    name: 'plano-de-acao/',
    components:{
      default: () => import('@/views/complaint/DenunciasPlano.vue'),
      NavBarEmpresa,
    }
  },
    {
      path: '/:company/denuncias/status',
      name: 'denuncias-status',
      components:{
        default: () => import('@/views/complaint/Status.vue'),
        NavBarEmpresa,
      },
      meta: { publicAccess: true }
    },
    {
      path: '/:company/denuncias/status-reclamacao',
      name: 'denuncias-status-reclamacao',
      components:{
        default: () => import('@/views/complaint/StatusReclamacao.vue'),
        NavBarEmpresa,
      },
      meta: { publicAccess: true }
    },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: () => {
      try {
        const last = localStorage.getItem('lastCliente');
        return last ? `/${last}` : '/';
      } catch (_) {
        return '/';
      }
    }
  }

]

const router = createRouter({
  base: '/site/' || '/',
  mode: 'history',
  history: createWebHistory(),
  // hash: true,
  routes
})

router.beforeEach(async (to, from, next) => {

  try {
    const now = Date.now();
    const sessionExpiresAtMs = Number(localStorage.getItem('sessionExpiresAtMs') || 0);
    const supportLoginExpiresAtMs = Number(localStorage.getItem('supportLoginExpiresAtMs') || 0);
    const supportSession = readSupportSession();

    if (supportSession) {
      const sessionExpired = (!supportLoginExpiresAtMs || now >= supportLoginExpiresAtMs || (supportSession.expiresAtMs && now >= supportSession.expiresAtMs));
      if (sessionExpired) {
        try {
          if (supportSession.company && supportSession.sessionId) {
            const supportAccountId = await resolveAccountIdBySlug(supportSession.company);
            if (supportAccountId) {
              await updateDoc(doc(db, `accounts/${supportAccountId}/sessions/${supportSession.sessionId}`), {
                endedAt: serverTimestamp(),
                endedReason: 'timeout'
              });
            }
          }
        } catch (_) { /* ignore */ }
        try {
          localStorage.removeItem('supportLoginExpiresAtMs');
          localStorage.removeItem('sessionExpiresAtMs');
        } catch (_) { /* ignore */ }
        await store.dispatch('logOut');
        if (to.path !== '/admin/suporte/login') return next('/admin/suporte/login');
      }
    } else if (sessionExpiresAtMs && now >= sessionExpiresAtMs) {
      try {
        localStorage.removeItem('sessionExpiresAtMs');
      } catch (_) { /* ignore */ }
      await store.dispatch('logOut');
      const slug = (to.params && (to.params.company || to.params.cliente)) || localStorage.getItem('lastCliente') || '';
      if (slug) return next(`/${slug}/entrar`);
      return next('/entrar');
    }
  } catch (_) { /* ignore */ }

  const pendingSSOData = (() => {
    try {
      const raw = localStorage.getItem('justLoggedInWithSSO');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.slug || !parsed.expiresAt) return null;
      if (Date.now() >= parsed.expiresAt) {
        localStorage.removeItem('justLoggedInWithSSO');
        return null;
      }
      return parsed.slug.toLowerCase();
    } catch (_) {
      try { localStorage.removeItem('justLoggedInWithSSO'); } catch (_) { /* ignore */ }
      return null;
    }
  })();
  const slugParam = (to.params && (to.params.company || to.params.cliente) || '').toString().trim().toLowerCase();
  const allowAfterSSO = pendingSSOData && slugParam === pendingSSOData;
  if (allowAfterSSO) {
    localStorage.removeItem('justLoggedInWithSSO');
    return next();
  }


  // Plataforma: admin global

  if (to.matched.some(r => r.meta && r.meta.requiresSupportUser)) {
    try {
      const expiresRaw = localStorage.getItem('supportLoginExpiresAtMs');
      const expiresAtMs = expiresRaw ? Number(expiresRaw) : 0;
      if (!expiresAtMs || Date.now() >= expiresAtMs) {
        localStorage.removeItem('supportLoginExpiresAtMs');
        await store.dispatch('logOut');
        return next('/admin/suporte/login');
      }

      const u = (store.getters && store.getters.user) || null;
      const data = u && (u.data || u);
      const email = (data?.email || u?.email || '').toLowerCase();
      if (!email) return next('/admin/suporte/login');

      const adminRef = doc(db, `configuracoes/admins/platform_admins/${email}`);
      const adminSnap = await getDoc(adminRef);
      const isAdmin = adminSnap.exists() && adminSnap.data() && adminSnap.data().active === true;
      if (isAdmin) return next();

      const supportRef = doc(db, `suporte/${email}`);
      const supportSnap = await getDoc(supportRef);
      const isSupport = supportSnap.exists() && supportSnap.data() && supportSnap.data().enabled === true;
      if (isSupport) return next();

      return next('/admin/suporte/login');
    } catch (_) {
      return next('/admin/suporte/login');
    }
  }
  if (to.matched.some(r => r.meta && r.meta.requiresPlatformAdmin)) {

    try {

      const u = (store.getters && store.getters.user) || null;

      const data = u && (u.data || u);

      const email = (data?.email || u?.email || '').toLowerCase();

      if (email) {

        const ref = doc(db, `configuracoes/admins/platform_admins/${email}`);

        const snap = await getDoc(ref);

        if (snap.exists() && snap.data() && snap.data().active === true) return next();

      }

      return next('/');

    } catch (_) {

      return next('/');

    }

  }



  // Cliente: exige modulo admin habilitado no config do usuario

  if (to.matched.some(r => r.meta && r.meta.requiresCompanyAdmin)) {

    try {

      const slug = (to.params.company || to.params.cliente || '').toLowerCase();

      const supportSession = readSupportSession();
      if (isSupportSessionForCompany(supportSession, slug)) {
        const impersonated = (supportSession && (supportSession.impersonatedDocId || supportSession.impersonatedEmail) || '').toString();
        if (!impersonated) return next(`/${slug}`);
        const supportUser = await fetchUserByEmail(slug, impersonated);
        const docId = supportUser ? supportUser.docId : '';
        if (!docId) return next(`/${slug}`);
        const accountId = await resolveAccountIdBySlug(slug);
        if (!accountId) return next(`/${slug}`);
        const cfgRef = doc(db, `accounts/${accountId}/users/${docId}/modules/config`);
        const cfgSnap = await getDoc(cfgRef);
        const cfg = cfgSnap.exists() ? (cfgSnap.data() || {}) : {};
        if (cfg.admin && cfg.admin.enabled === true) return next();
        return next(`/${slug}`);
      }


      const u = (store.getters && store.getters.user) || null;

      const data = u && (u.data || u);

      const email = (data?.email || u?.email || '').toLowerCase();

      if (!slug || !email) return next(slug ? `/${slug}/entrar` : '/');

      const accountId = await resolveAccountIdBySlug(slug);
      if (!accountId) return next(`/${slug}/entrar`);
      const cfgRef = doc(db, `accounts/${accountId}/users/${email}/modules/config`);

      const cfgSnap = await getDoc(cfgRef);

      const cfg = cfgSnap.exists() ? (cfgSnap.data() || {}) : {};

      if (cfg.admin && cfg.admin.enabled === true) return next();

      return next(`/${slug}`);

    } catch (_) {

      const slug = (to.params.company || to.params.cliente || '').toLowerCase();

      return next(slug ? `/${slug}/entrar` : '/');

    }

  }



  // Aplicar tema do cliente em todas as rotas com slug
  try {
    const slug = (to.params && (to.params.company || to.params.cliente) || '').toString().trim().toLowerCase();
    if (slug) await store.dispatch('setCompany', slug);
  } catch (_) { /* ignore */ }

  // Garantir usuario do Firebase no store antes de renderizar menus
  try {
    const firebaseUser = auth.currentUser;
    if (firebaseUser && !store.getters?.user) {
      store.commit('SET_USER', firebaseUser);
      if (firebaseUser.email) store.commit('SET_EMAIL', firebaseUser.email);
    }
  } catch (_) { /* ignore */ }

  // Persistir ultimo cliente acessado
  try {
    const slug = (to.params && (to.params.company || to.params.cliente) || '').toString().trim().toLowerCase();
    if (slug) localStorage.setItem('lastCliente', slug);
  } catch (_) { /* ignore */ }

  // Permitir acesso público em rotas específicas (ex.: fornecedor)
  if (to.matched.some(r => r.meta && r.meta.publicAccess)) {
    return next();
  }

  // Exigir login para rotas de cliente via guarda centralizada.
  try {
    const decision = await evaluateCompanyRouteAccess(to);
    if (!decision.allow && decision.redirect) return next(decision.redirect);
  } catch (_) { /* ignore and fall through */ }

  next();

})

router.onError((error) => {
  console.error('[ROUTER] navigation error', error);
});

export default router
